import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Loader2, RefreshCw, ShieldCheck, X, Zap } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { ago } from "../../lib/dnsProviders";
import DomainRecords from "./DomainRecords";

const BADGE = {
  live: ["Live", "text-emerald-700 border-emerald-200 bg-emerald-50"],
  verified: ["Needs credits", "text-amber-700 border-amber-200 bg-amber-50"],
  offline: ["Paused", "text-slate-600 border-slate-200 bg-slate-100"],
  failed: ["Problem", "text-rose-700 border-rose-200 bg-rose-50"],
  pending: ["Waiting for DNS", "text-amber-700 border-amber-200 bg-amber-50"],
  legacy: ["Old setup", "text-slate-600 border-slate-200 bg-slate-100"],
};

const Step = ({ n, title, children, testid }) => (
  <div className="flex gap-3" data-testid={testid}>
    <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-sky-100 text-sky-700 text-[11px] font-semibold flex items-center justify-center">{n}</span>
    <div className="flex-1 min-w-0 space-y-2"><p className="text-sm font-medium text-slate-800">{title}</p>{children}</div>
  </div>
);

const AddDomainForm = ({ onSubmit, busy, price }) => {
  const [domain, setDomain] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(domain); }} className="space-y-2">
      <div className="flex gap-2">
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="yourbrand.com or app.yourbrand.com" data-testid="domain-input"
          className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-800 outline-none focus:border-sky-400 focus:bg-white transition-colors" />
        <button type="submit" disabled={busy || domain.trim().length < 4} data-testid="domain-connect-btn"
          className="h-10 px-4 rounded-lg bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50 flex items-center gap-2 transition-colors">
          {busy && <Loader2 size={14} className="animate-spin" />} Connect
        </button>
      </div>
      <p className="text-xs text-slate-500">Use a domain you already own. You add one record and it goes live by itself, with free SSL.</p>
      {(price.setup > 0 || price.monthly > 0) && (
        <p className="text-xs text-slate-500" data-testid="domain-price-note">
          Costs <b className="text-slate-700">{price.setup} credits</b> once + <b className="text-slate-700">{price.monthly} credits a month</b> while live — taken when it goes live, then on the 1st of each month.
        </p>
      )}
    </form>
  );
};

const Header = ({ cd, onRemove, busy }) => {
  const key = !cd.domainee_id ? "legacy" : cd.billing_paused && !cd.live ? "offline" : cd.status;
  const [label, cls] = BADGE[key] || BADGE.pending;
  return (
    <div className="flex items-start gap-2">
      <ShieldCheck size={16} className={`mt-0.5 shrink-0 ${cd.live ? "text-emerald-600" : "text-slate-400"}`} />
      <p className="min-w-0 text-sm text-slate-900 font-medium truncate" data-testid="domain-name">{cd.root_domain || cd.domain}</p>
      <span className={`ml-auto shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded border ${cls}`} data-testid="domain-status">{label}</span>
      <button onClick={onRemove} disabled={busy} data-testid="domain-remove-btn" className="text-slate-400 hover:text-rose-600 transition-colors" aria-label="Remove domain"><X size={15} /></button>
    </div>
  );
};

const Live = ({ cd, published, monthly }) => (
  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 space-y-1" data-testid="domain-live-note">
    <p>Your app is live at <a href={`https://${cd.domain}`} target="_blank" rel="noreferrer" className="font-semibold underline">{cd.domain}</a> with SSL.</p>
    {monthly > 0 && <p className="text-emerald-700" data-testid="domain-renews-note">Renews on the 1st of each month for {monthly} credits.</p>}
    {!published && <p className="text-amber-700">Publish your app so the domain has something to show.</p>}
  </div>
);

const NeedsCredits = ({ n, paused, busy, onCheck }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-2" data-testid="domain-needs-credits">
    <p data-testid="domain-needs-credits-text">
      {paused
        ? <>Your domain is paused — this month's <b>{n} credits</b> couldn't be taken. Top up and it comes back online automatically within a few minutes.</>
        : <>Your DNS is ready. Going live costs {n > 0 ? <b>{n} credits</b> : "the domain fee"} — top up, then tap Check now (we also check every few minutes).</>}
    </p>
    <div className="flex gap-2">
      <Link to="/billing" className="inline-flex h-8 px-3 items-center rounded-lg bg-slate-900 text-white font-medium" data-testid="domain-topup-link">Top up credits</Link>
      <button onClick={onCheck} disabled={busy} data-testid="domain-needs-credits-check" className="h-8 px-3 rounded-lg border border-amber-300 font-medium hover:bg-amber-100 disabled:opacity-60 inline-flex items-center gap-1.5">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Check now
      </button>
    </div>
  </div>
);

const Paused = () => (
  <p className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600" data-testid="domain-paused-note">
    This domain has been paused by support. Contact support@ishukart.org to bring it back.
  </p>
);

const Legacy = ({ cd, paid, price, busy, onReconnect }) => (
  <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-slate-700 space-y-2" data-testid="domain-legacy-note">
    <p>This domain uses our old setup. Reconnect it to switch to the new one — automatic SSL and it goes live by itself. You'll get one new record to add.</p>
    {cd.live && <p className="text-emerald-700" data-testid="domain-legacy-stays-live">Your site stays online while you switch.</p>}
    <button onClick={onReconnect} disabled={busy} data-testid="domain-reconnect-btn"
      className="w-full h-9 rounded-lg bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} Reconnect automatically
    </button>
    {price.loaded && <p className="text-[11px] text-slate-500" data-testid="domain-reconnect-price">
      {paid || price.setup <= 0 ? "No setup fee — " : `${price.setup} credits once + `}{price.monthly > 0 ? `${price.monthly} credits a month once it's live.` : "no monthly fee."}
    </p>}
  </div>
);

const OneClick = ({ cd, onBack }) => (
  <button type="button" data-testid="domain-one-click-btn"
    onClick={() => { window.open(cd.one_click_url, "domain-connect", "width=750,height=750"); window.addEventListener("focus", onBack, { once: true }); }}
    className="w-full h-10 rounded-lg bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 flex items-center justify-center gap-2 transition-colors">
    <Zap size={14} /> Connect automatically with {cd.provider}
  </button>
);

const Instructions = ({ cd }) => (
  <>
    <DomainRecords records={cd.records || []} />
    {cd.steps?.length > 0 && <ol className="text-[11px] text-slate-600 space-y-1 list-decimal pl-4" data-testid="domain-steps">{cd.steps.map((s) => <li key={s}>{s}</li>)}</ol>}
    {cd.quirks?.length > 0 && <ul className="text-[11px] text-amber-700 space-y-1 list-disc pl-4" data-testid="domain-quirks">{cd.quirks.map((q) => <li key={q}>{q}</li>)}</ul>}
    {cd.dashboard_url && (
      <a href={cd.dashboard_url} target="_blank" rel="noreferrer" data-testid="domain-provider-link" className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:underline">
        Open {cd.provider || "your DNS provider"} <ExternalLink size={12} />
      </a>
    )}
  </>
);

const Pending = ({ cd, busy, onCheck }) => (
  <>
    {cd.legacy_live && <p className="text-xs text-emerald-700" data-testid="domain-switching-note">Your site stays online on the old setup until the new record works.</p>}
    {cd.warnings?.map((w) => <p key={w} className="text-xs text-rose-600" data-testid="domain-warning">{w}</p>)}
    {cd.one_click_url && <Step n={1} title="Fastest: approve it at your DNS provider" testid="domain-step-oneclick"><OneClick cd={cd} onBack={onCheck} /></Step>}
    <Step n={cd.one_click_url ? 2 : 1} title={cd.one_click_url ? "Or add this record yourself" : "Add this record where you bought your domain"} testid="domain-step-records"><Instructions cd={cd} /></Step>
    <Step n={cd.one_click_url ? 3 : 2} title="That's it — it goes live by itself" testid="domain-step-check">
      <button onClick={onCheck} disabled={busy} data-testid="domain-verify-btn"
        className="w-full h-9 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Check now
      </button>
      <p className="text-[11px] text-slate-500" data-testid="domain-last-checked">{cd.monitor_message || "We check automatically every few minutes."} · last checked {ago(cd.checked_at)}</p>
    </Step>
  </>
);

const ApexRedirect = ({ r, busy, onCheck }) => {
  if (!r || r.error) return null;
  if (r.verified) return <p className="text-xs text-emerald-700" data-testid="domain-apex-live">{r.domain} also opens your site (forwards to www).</p>;
  return (
    <div className="space-y-2 border-t border-slate-200 pt-3" data-testid="domain-apex-panel">
      <p className="text-sm font-medium text-slate-800">Make <span className="font-mono">{r.domain}</span> work too <span className="text-xs font-normal text-slate-400">(optional, free)</span></p>
      <p className="text-[11px] text-slate-500">Add these records so people who type the address without “www” land on your site. Delete any other A records for “@” first (domain companies often add parking ones).</p>
      <DomainRecords records={r.records || []} />
      <button onClick={onCheck} disabled={busy} data-testid="domain-apex-check-btn" className="h-8 px-3 rounded-lg border border-slate-300 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 inline-flex items-center gap-1.5">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Check now
      </button>
      {r.checked_at && <p className="text-[11px] text-slate-500" data-testid="domain-apex-status">{r.monitor_message || "Still waiting for these records."} · last checked {ago(r.checked_at)}</p>}
    </div>
  );
};

export const DomainSection = ({ project, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState({ setup: 0, monthly: 0, loaded: false });
  const cd = project.custom_domain;
  useEffect(() => { api.creditsPricing().then((r) => setPrice({ setup: r.domain_credits || 0, monthly: r.domain_monthly_credits || 0, loaded: true })).catch(() => {}); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); onChange(r.project || r); ok?.(r); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const check = useCallback((quiet) => run(() => api.verifyDomain(project.id), (r) => {
    if (quiet) return;
    if (r.live) toast.success("Your domain is live");
    else toast("Not there yet", { description: r.message || "DNS changes can take a while — we'll keep checking." });
  }), [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const waiting = cd?.domainee_id && cd.status !== "offline" && (!cd.live || cd.legacy_live || (cd.redirect && !cd.redirect.verified && !cd.redirect.error));
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => check(true), 30000);
    return () => clearInterval(t);
  }, [waiting, check]);

  if (!cd) return <AddDomainForm busy={busy} price={price} onSubmit={(d) => run(() => api.setDomain(project.id, d))} />;
  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid="domain-panel">
      <Header cd={cd} busy={busy} onRemove={() => run(() => api.removeDomain(project.id))} />
      {!cd.domainee_id ? <Legacy cd={cd} paid={(project.paid_domains || []).includes(cd.domain)} price={price} busy={busy}
        onReconnect={() => run(() => api.setDomain(project.id, cd.domain), () => toast.success("Reconnected — add the new record below"))} />
        : cd.live && !cd.legacy_live ? <Live cd={cd} published={!!project.published_version_id} monthly={price.monthly} />
        : cd.status === "offline" ? <Paused />
        : cd.status === "verified" ? <NeedsCredits n={cd.needs_credits || price.setup + price.monthly} paused={!!cd.billing_paused} busy={busy} onCheck={() => check(false)} />
        : <Pending cd={cd} busy={busy} onCheck={() => check(false)} />}
      {cd.domainee_id && cd.status !== "offline" && <ApexRedirect r={cd.redirect} busy={busy} onCheck={() => check(true)} />}
    </div>
  );
};

export default DomainSection;
